import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/brand/Logo";
import {
    User,
    Palette,
    Lock,
    Monitor,
    Bell,
    Plug,
    Info,
} from "lucide-react";

const SETTINGS_NAV = [
    { path: "profile", label: "Profile", icon: User },
    { path: "appearance", label: "Appearance", icon: Palette },
    { path: "security", label: "Security", icon: Lock },
    { path: "sessions", label: "Sessions", icon: Monitor },
    { path: "notifications", label: "Notification preferences", icon: Bell },
    { path: "integrations", label: "Integrations", icon: Plug },
    { path: "about", label: "About YugOS", icon: Info },
];

export function SettingsShell() {
    return (
        <div data-testid="page-settings">
            <PageHeader title="Settings" description="Workspace, appearance, security, and integrations." />
            <div className="p-8 max-w-[1400px]">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr]">
                    <aside className="space-y-0.5" data-testid="settings-nav">
                        {SETTINGS_NAV.map((item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`settings-nav-${item.path}`}
                                    className={({ isActive }) =>
                                        cn(
                                            "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors",
                                            isActive
                                                ? "bg-zinc-900 text-white"
                                                : "text-zinc-700 hover:bg-zinc-100",
                                        )
                                    }
                                >
                                    <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    {item.label}
                                </NavLink>
                            );
                        })}
                    </aside>
                    <div className="rounded-md border border-zinc-200 bg-white p-6">
                        <Outlet />
                    </div>
                </div>
            </div>
        </div>
    );
}

export function SettingsIndex() {
    return <Navigate to="profile" replace />;
}

export function SettingsProfile() {
    const { user, setUser } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [avatar, setAvatar] = useState(user?.avatar_url || "");
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.patch("/me", { name, avatar_url: avatar || null });
            setUser(data);
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;
    return (
        <form onSubmit={save} className="space-y-6" data-testid="settings-profile">
            <SectionTitle title="Profile" description="How you appear across YugOS." />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Full name">
                    <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
                </Field>
                <Field label="Email">
                    <Input value={user.email} disabled />
                </Field>
                <Field label="Role">
                    <div>
                        <Badge variant="secondary" className="uppercase tracking-widest text-[10px]">
                            {user.role}
                        </Badge>
                    </div>
                </Field>
                <Field label="Avatar URL (optional)">
                    <Input value={avatar} onChange={(e) => setAvatar(e.target.value)} placeholder="https://…" />
                </Field>
            </div>
            <div className="flex justify-end pt-2 border-t border-zinc-100">
                <Button type="submit" disabled={saving} data-testid="profile-save">
                    {saving ? "Saving…" : "Save changes"}
                </Button>
            </div>
        </form>
    );
}

export function SettingsAppearance() {
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
    const toggle = (v) => {
        setDark(v);
        document.documentElement.classList.toggle("dark", v);
    };
    return (
        <div className="space-y-6" data-testid="settings-appearance">
            <SectionTitle title="Appearance" description="Theme and density preferences." />
            <div className="flex items-center justify-between rounded-md border border-zinc-200 p-3">
                <div>
                    <div className="text-sm font-medium">Dark mode</div>
                    <div className="text-xs text-zinc-500">Architecture-ready. Toggle to preview.</div>
                </div>
                <Switch checked={dark} onCheckedChange={toggle} data-testid="appearance-dark-toggle" />
            </div>
            <div className="rounded-md border border-dashed border-zinc-200 p-6 text-sm text-zinc-500 text-center">
                Additional appearance controls (density, accent) will land in a future release.
            </div>
        </div>
    );
}

export function SettingsSecurity() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next !== confirm) return toast.error("Passwords do not match");
        if (next.length < 8) return toast.error("Password must be at least 8 characters");
        setLoading(true);
        try {
            await api.post("/me/change-password", { current_password: current, new_password: next });
            toast.success("Password changed");
            setCurrent(""); setNext(""); setConfirm("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-6" data-testid="settings-security">
            <SectionTitle title="Security" description="Change your password." />
            <div className="grid grid-cols-1 gap-4 max-w-md">
                <Field label="Current password">
                    <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="security-current" />
                </Field>
                <Field label="New password">
                    <Input type="password" required value={next} onChange={(e) => setNext(e.target.value)} data-testid="security-next" />
                </Field>
                <Field label="Confirm new password">
                    <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="security-confirm" />
                </Field>
            </div>
            <div className="flex justify-end pt-2 border-t border-zinc-100">
                <Button type="submit" disabled={loading} data-testid="security-save">
                    {loading ? "Saving…" : "Update password"}
                </Button>
            </div>
        </form>
    );
}

export function SettingsSessions() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/me/sessions");
            setSessions(data.sessions || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);
    const revoke = async (id) => {
        await api.delete(`/me/sessions/${id}`);
        toast.success("Session revoked");
        load();
    };
    return (
        <div className="space-y-6" data-testid="settings-sessions">
            <SectionTitle title="Active sessions" description="Devices where you are signed in." />
            <div className="rounded-md border border-zinc-200 divide-y">
                {loading ? (
                    <div className="p-6 text-sm text-zinc-500 text-center">Loading…</div>
                ) : sessions.length === 0 ? (
                    <div className="p-6 text-sm text-zinc-500 text-center">No active sessions.</div>
                ) : (
                    sessions.map((s) => (
                        <div key={s.id} className="flex items-center justify-between p-3">
                            <div>
                                <div className="text-sm font-medium truncate max-w-md">{s.user_agent || "Unknown device"}</div>
                                <div className="text-xs text-zinc-500 font-mono">
                                    {s.ip} · Last seen {new Date(s.last_seen_at).toLocaleString()}
                                </div>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => revoke(s.id)} data-testid={`revoke-${s.id}`}>
                                Revoke
                            </Button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export function SettingsNotifications() {
    const [prefs, setPrefs] = useState({ email: true, ai: true, system: true });
    return (
        <div className="space-y-6" data-testid="settings-notifications">
            <SectionTitle title="Notification preferences" description="Choose which notifications reach you." />
            {[
                { key: "email", label: "Email digests", desc: "Daily summary of activity" },
                { key: "ai", label: "AI recommendations", desc: "Signals from Yug Brain" },
                { key: "system", label: "System updates", desc: "Product changes & maintenance" },
            ].map((row) => (
                <div key={row.key} className="flex items-center justify-between border-b border-zinc-100 pb-4">
                    <div>
                        <div className="text-sm font-medium">{row.label}</div>
                        <div className="text-xs text-zinc-500">{row.desc}</div>
                    </div>
                    <Switch
                        checked={prefs[row.key]}
                        onCheckedChange={(v) => setPrefs({ ...prefs, [row.key]: v })}
                        data-testid={`notif-${row.key}`}
                    />
                </div>
            ))}
            <div className="text-[11px] text-zinc-500">Preferences are stored locally in this preview.</div>
        </div>
    );
}

export function SettingsIntegrations() {
    const items = [
        { name: "WhatsApp Business", desc: "Order updates & customer conversations" },
        { name: "Tally / Zoho Books", desc: "Sync ledgers automatically" },
        { name: "Shiprocket", desc: "Dispatch and tracking" },
        { name: "Google Workspace", desc: "SSO & directory sync" },
    ];
    return (
        <div className="space-y-6" data-testid="settings-integrations">
            <SectionTitle title="Integrations" description="Connect YugOS to the tools you already use." />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {items.map((i) => (
                    <div key={i.name} className="rounded-md border border-zinc-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-sm font-medium text-zinc-900">{i.name}</div>
                                <div className="mt-0.5 text-xs text-zinc-500">{i.desc}</div>
                            </div>
                            <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">
                                Soon
                            </Badge>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function SettingsAbout() {
    return (
        <div className="space-y-4" data-testid="settings-about">
            <SectionTitle title="About YugOS" description="The commerce intelligence platform for Yug Traders." />
            <div className="rounded-md border border-zinc-200 p-4 text-sm">
                <div className="flex items-center gap-3">
                    <Logo size={36} />
                    <div>
                        <div className="font-display font-semibold">YugOS</div>
                        <div className="text-xs text-zinc-500 font-mono">v0.1.0 · Foundation</div>
                    </div>
                </div>
                <p className="mt-4 text-sm text-zinc-600 leading-relaxed">
                    YugOS is the operating system for modern wholesale and retail businesses. It replaces the memory of
                    people with the memory of the business — and lets AI reason on top.
                </p>
            </div>
        </div>
    );
}

function SectionTitle({ title, description }) {
    return (
        <div>
            <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-zinc-500">{description}</p>}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs">{label}</Label>
            {children}
        </div>
    );
}
